import { useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface BlankPageDetectorProps {
  children: React.ReactNode;
  timeout?: number;
}

export function BlankPageDetector({ children, timeout = 5000 }: BlankPageDetectorProps) {
  const [showFallback, setShowFallback] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Check if the page appears blank after timeout
    const timer = setTimeout(() => {
      const body = document.body;
      const hasVisibleContent = body.children.length > 0 && 
        body.scrollHeight > 100; // Basic check for content

      if (!hasVisibleContent) {
        setShowFallback(true);
      }
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout]);

  useEffect(() => {
    // Check for JavaScript errors that might cause blank pages
    const handleError = (event: ErrorEvent) => {
      Sentry.captureException(event.error, {
        tags: { context: 'js_error', component: 'BlankPageDetector' },
        extra: { message: 'JavaScript error detected' }
      });
      setShowFallback(true);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      Sentry.captureException(event.reason, {
        tags: { context: 'promise_rejection', component: 'BlankPageDetector' },
        extra: { message: 'Unhandled promise rejection' }
      });
      setShowFallback(true);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (showFallback) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold text-destructive">Page Loading Issue</h2>
            <p className="text-sm text-muted-foreground">
              The page appears to be blank or failed to load properly. This could be due to:
            </p>
            <ul className="text-xs text-muted-foreground text-left space-y-1">
              <li>• Network connectivity issues</li>
              <li>• JavaScript loading errors</li>
              <li>• Authentication system problems</li>
              <li>• Server configuration issues</li>
            </ul>
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              URL: {window.location.href}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}